import React, { useState, useCallback } from 'react';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@material-ui/core/Icon';
import Toolbar from '@material-ui/core/Toolbar';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { KeyboardDatePicker, KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import SearchIcon from '@material-ui/icons/Search';
import SystemUpdateAltIcon from '@material-ui/icons/SystemUpdateAlt';
import { WithTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import DateFnsUtils from '@date-io/date-fns';
import frLocale from 'date-fns/locale/fr';
// Components
import { TooltipButton } from '../tooltip-button';
// Ressources
import { IntervalKeys } from '../../local-types';

interface EnhancedTableToolbarProps extends WithTranslation {
    uuidTable: string;
    date?: string; //Key of start filter
    dateEnd?: string; //Key of end filter if none will be date's one
    intervalLabel?: string;
    intervalKeys?: IntervalKeys;
    intervalDateTime?: boolean;
    filter?: any;
    actions?: JSX.Element | JSX.Element[];
    add?: string;
    onExport?: (startDate: Date, endDate: Date) => void;
}

const EnhancedTableToolbar = ({
    t,
    uuidTable,
    date,
    dateEnd,
    intervalLabel,
    intervalKeys,
    intervalDateTime = false,
    filter,
    actions,
    add,
    onExport,
}: EnhancedTableToolbarProps): JSX.Element => {
    const classes = useToolbarStyles();
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    const handleStartDateChange = useCallback(
        (date: Date): void => {
            if (date && !startDate) {
                date.setHours(0, 0, 0, 0);
            }
            setStartDate(date);
        },
        [startDate],
    );

    const handleEndDateChange = useCallback(
        (date: Date): void => {
            if (date && !endDate) {
                date.setHours(23, 59, 59, 0);
            }
            setEndDate(date);
        },
        [endDate],
    );

    const handleExport = useCallback(() => {
        if (onExport) {
            onExport(startDate!, endDate!);
        }
    }, [startDate, endDate, onExport]);

    if (!date && !actions && !add) {
        return null;
    }

    const getDefaultDate = (key: string, type: string) => {
        return filter?.[`${type}[${key}]`] || null;
    };
    const startDateDefault = getDefaultDate(date!, 'after');
    const endDateDefault = getDefaultDate(dateEnd ?? date!, 'before');

    const DateElement = intervalDateTime ? KeyboardDateTimePicker : KeyboardDatePicker;

    const sharedConfig: any = {
        disableToolbar: true,
        variant: 'inline',
        format: intervalDateTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy',
        className: classes.dateField,
        inputProps: { form: uuidTable },
        invalidDateMessage: t('invalidDateMessage'),
        ...(intervalDateTime ? { ampm: false } : { autoOk: true }),
    };

    return (
        <Toolbar className={classes.root}>
            <div className={classes.container}>
                {(date || actions || add) && (
                    <div className={classes.dateActionsWrapper}>
                        {date && (
                            <div className={classes.dateWrapper}>
                                <div className={classes.dateLabel}>{`${intervalLabel} ${t(
                                    'filterDateHint',
                                ).toLowerCase()}`}</div>
                                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
                                    <DateElement
                                        {...sharedConfig}
                                        id="start-date"
                                        label={t('startDate')}
                                        name={intervalKeys?.start || `after[${date}]`}
                                        value={startDate || startDateDefault}
                                        onChange={handleStartDateChange}
                                        KeyboardButtonProps={{
                                            'aria-label': t('startDate'),
                                        }}
                                    />
                                    <div className={classes.dateSeparator}>{t('and')}</div>
                                    <DateElement
                                        {...sharedConfig}
                                        id="end-date"
                                        label={t('endDate')}
                                        name={intervalKeys?.end || `before[${dateEnd ?? date}]`}
                                        value={endDate || endDateDefault}
                                        onChange={handleEndDateChange}
                                        KeyboardButtonProps={{
                                            'aria-label': t('endDate'),
                                        }}
                                    />
                                </MuiPickersUtilsProvider>
                                <TooltipButton title={t('searchTooltipAction')}>
                                    <IconButton aria-label="filter list" type="submit" form={uuidTable}>
                                        <SearchIcon />
                                    </IconButton>
                                </TooltipButton>
                                {onExport && (
                                    <TooltipButton title={t('exportTooltip')}>
                                        <IconButton aria-label="export list" onClick={handleExport}>
                                            <SystemUpdateAltIcon />
                                        </IconButton>
                                    </TooltipButton>
                                )}
                            </div>
                        )}
                        {actions}
                        {add && (
                            <Button
                                to={add}
                                color="secondary"
                                variant="contained"
                                component={RouterLink}
                                aria-label={t('common:add')}
                                startIcon={<Icon>add</Icon>}
                            >
                                <b>{t('common:add')}</b>
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </Toolbar>
    );
};

const useToolbarStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flex: 1,
            padding: theme.spacing(2),
            paddingBottom: 0,
        },
        container: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
        },
        dateActionsWrapper: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            flex: 1,
        },
        dateWrapper: {
            display: 'flex',
            alignItems: 'center',
        },
        dateLabel: {
            marginRight: 16,
        },
        dateField: {
            marginBottom: theme.spacing(2),
        },
        dateSeparator: {
            margin: '0 16px',
        },
    }),
);

export default React.memo(EnhancedTableToolbar);
